// ============================================================
// KVKK gösterim bileşenleri (§9)
// ============================================================

import { card, emptyNote, sectionTitle } from "./ui.js";
import { avatar } from "../lib/avatar.js";
import { esc, fmtDate } from "../lib/fmt.js";
import {
  AYDINLATMA_METNI,
  RIZA_TURU,
  SAKLAMA_SURESI,
  eksikRizalar,
  fotografKullanilabilir,
  kvkkOzeti,
  rizalar,
  saklamaSuresiDolanlar,
} from "../domain/kvkk.js";

/** Öğrenci modalindeki rıza bloğu. */
export function rizaBlogu(student) {
  const list = rizalar(student);
  const eksikZorunlu = list.filter(r => r.zorunlu && !r.granted);

  return `
  <div class="rounded-xl ${eksikZorunlu.length ? "border-2 border-coral bg-coral-soft" : "bg-paper"} p-3 col-span-2">
    <div class="flex items-center gap-2">
      <span class="text-[11px] font-bold uppercase ${eksikZorunlu.length ? "text-coral" : "text-inksoft"}">
        Veli Rızaları (KVKK)
      </span>
      ${eksikZorunlu.length
        ? `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5">${eksikZorunlu.length} ZORUNLU EKSİK</span>`
        : ""}
    </div>

    <ul class="mt-2 space-y-1">
      ${list.map(r => `
        <li class="flex items-start gap-2 text-xs">
          <span class="shrink-0 ${r.granted ? "text-teal" : r.zorunlu ? "text-coral" : "text-inksoft"}">
            ${r.granted ? "✓" : "✕"}
          </span>
          <span class="min-w-0 flex-1">
            <span class="font-semibold">${esc(r.label)}</span>
            ${r.zorunlu ? `<span class="text-[9px] font-bold rounded bg-ink text-white px-1 ml-1">ZORUNLU</span>` : ""}
            <span class="block text-[11px] text-inksoft">
              ${r.granted && r.at
                ? `${esc(r.by ?? "—")} · ${fmtDate(r.at)}`
                : r.at ? `Reddedildi · ${fmtDate(r.at)}` : "Kayıt yok"}
            </span>
          </span>
        </li>`).join("")}
    </ul>

    ${!fotografKullanilabilir(student, "photoSocial") && student.photo ? `
      <div class="text-[11px] text-coral font-semibold mt-2 pt-2 border-t border-line/70">
        Bu çocuğun fotoğrafı sosyal medyada paylaşılamaz.
      </div>` : ""}
  </div>`;
}

/** Ayarlar'daki KVKK yönetim kartı. */
export function kvkkKarti(branchId) {
  const o = kvkkOzeti(branchId);
  const eksik = eksikRizalar(branchId);
  const saklama = saklamaSuresiDolanlar(branchId);

  return card(`
    <div class="p-5">
      ${sectionTitle(
        "KVKK & Veri Koruma",
        o.eksikZorunluRiza
          ? `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${o.eksikZorunluRiza} eksik rıza</span>`
          : `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Rızalar tam</span>`,
      )}

      <p class="text-[11px] text-inksoft mb-3">
        Sistem çocukların sağlık verisini ve görselini işliyor — bunlar özel
        nitelikli kişisel veridir. Her amaç için ayrı rıza gerekir.
      </p>

      <!-- Rıza dağılımı -->
      <div class="space-y-2">
        ${Object.entries(RIZA_TURU).map(([kod, tanim]) => {
          const sayi = o.rizaSayaci[kod];
          const oran = o.toplamOgrenci ? Math.round((sayi / o.toplamOgrenci) * 100) : 0;
          return `
          <div>
            <div class="flex items-center justify-between gap-2 text-xs">
              <span class="truncate">
                ${esc(tanim.label)}
                ${tanim.zorunlu ? `<span class="text-[9px] font-bold rounded bg-ink text-white px-1 ml-1">ZORUNLU</span>` : ""}
              </span>
              <span class="shrink-0 font-semibold">${sayi}/${o.toplamOgrenci}</span>
            </div>
            <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
              <div class="h-full rounded-full ${tanim.zorunlu && oran < 100 ? "bg-coral" : "bg-teal"}" style="width:${oran}%"></div>
            </div>
          </div>`;
        }).join("")}
      </div>

      <!-- Eksik rızalar -->
      ${eksik.length ? `
        <div class="mt-4 pt-3 border-t border-line">
          <div class="text-[11px] font-bold text-coral uppercase mb-2">Zorunlu Rızası Eksik Öğrenciler</div>
          <ul class="space-y-1.5">
            ${eksik.map(({ student, eksik: e }) => `
              <li data-student="${student.id}" class="flex items-center gap-2 rounded-xl border border-coral px-3 py-2 cursor-pointer hover:bg-coral-soft/40">
                ${avatar(student, "w-7 h-7")}
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(student.name)}</span>
                  <span class="block text-[11px] text-coral truncate">${e.map(x => esc(x.label)).join(", ")}</span>
                </span>
              </li>`).join("")}
          </ul>
          <p class="text-[11px] text-inksoft mt-2">
            Bu çocukların sağlık verisi işlenmemeli; evrak tamamlanana kadar
            yalnızca acil durum bilgisi kullanılabilir.
          </p>
        </div>` : ""}

      <!-- Saklama -->
      <div class="mt-4 pt-3 border-t border-line">
        <div class="text-[11px] font-bold text-inksoft uppercase mb-2">Saklama Süreleri</div>
        <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          ${Object.entries(SAKLAMA_SURESI).map(([kod, yil]) => `
            <div class="flex justify-between gap-2">
              <span class="text-inksoft truncate">${esc(SAKLAMA_ETIKET[kod] ?? kod)}</span>
              <span class="shrink-0 font-semibold">${yil} yıl</span>
            </div>`).join("")}
        </div>
        ${saklama.length
          ? `<div class="rounded-xl bg-sun-soft text-[#B07E14] text-xs font-semibold p-3 mt-2">
              ${saklama.length} ayrılmış öğrencinin saklama süresi doldu — verisi silinmeli.
            </div>`
          : `<p class="text-[11px] text-inksoft mt-2">Saklama süresi dolan kayıt yok.</p>`}
      </div>

      <!-- Aydınlatma metni -->
      <details class="mt-4 pt-3 border-t border-line">
        <summary class="text-[11px] font-bold text-inksoft uppercase cursor-pointer">Aydınlatma Metni</summary>
        <p class="text-[11px] text-inksoft whitespace-pre-line mt-2 leading-relaxed">${esc(AYDINLATMA_METNI)}</p>
      </details>
    </div>`, "min-w-0 lg:col-span-2");
}

const SAKLAMA_ETIKET = {
  ogrenciDosyasi: "Öğrenci dosyası",
  saglikKaydi: "Sağlık kaydı",
  olayTutanagi: "Olay tutanağı",
  maliKayit: "Mali kayıt",
  fotograf: "Fotoğraf",
  denetimIzi: "Denetim izi",
};

/** Veri dökümünü tarayıcıdan indirir (veri taşınabilirliği hakkı). */
export function dokumIndir(dosya) {
  const blob = new Blob([dosya.icerik], { type: dosya.tur });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dosya.dosyaAdi;
  a.click();
  URL.revokeObjectURL(url);
}

export { emptyNote };
